import { Link } from "react-router-dom";
import { getTripPhase, type TripPhase } from "../utils/getTripPhase";

interface ProgramItem {
  id: string;
  title: string;
  start_time: string | null;
  end_time: string | null;
  location?: string | null;
}

interface Trip {
  id: string;
  start_date: string;
  end_date: string;
}

interface PhaseContextCardProps {
  trip: Trip;
  programItems?: ProgramItem[];
  isOrganizer: boolean;
}

interface CardContent {
  phaseLabel: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaRoute: string;
  urgency: "low" | "medium" | "high";
}

function getDaysUntil(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.ceil((targetStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
}

function getDaysSince(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.floor((todayStart.getTime() - targetStart.getTime()) / (1000 * 60 * 60 * 24));
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
}

function getNextProgramItem(items: ProgramItem[]): ProgramItem | null {
  const now = new Date();
  return (
    [...items]
      .filter((item) => !!item.end_time && new Date(item.end_time) > now)
      .sort((a, b) => new Date(a.start_time!).getTime() - new Date(b.start_time!).getTime())[0] ?? null
  );
}

function getCardContent(phase: TripPhase, trip: Trip, programItems: ProgramItem[]): CardContent {
  const daysToStart = getDaysUntil(trip.start_date);
  const daysAfterEnd = getDaysSince(trip.end_date);
  const nextItem = getNextProgramItem(programItems);

  switch (phase) {
    case "pre_trip_early":
      return {
        phaseLabel: "Planung läuft",
        title: "Noch " + daysToStart + " Tage bis zur Reise",
        description: "Jetzt ist der beste Moment, das Programm zu ergänzen und Aktivitäten zu planen.",
        ctaLabel: "Programm ergänzen →",
        ctaRoute: `/trip/${trip.id}?tab=program`,
        urgency: "low",
      };

    case "pre_trip_late":
      return {
        phaseLabel: "Noch " + daysToStart + " Tage",
        title: "Fast Zeit zum Packen!",
        description: "Offene Aufgaben erledigen und sicherstellen, dass alle Infos aktuell sind.",
        ctaLabel: "Aufgaben checken →",
        ctaRoute: `/trip/${trip.id}?tab=overview`,
        urgency: "medium",
      };

    case "pre_trip_final":
      return {
        phaseLabel: daysToStart === 1 ? "Morgen geht's los!" : "Noch " + daysToStart + " Tage",
        title: "Fast so weit!",
        description: "Letzte Vorbereitungen treffen. Alles dabei?",
        ctaLabel: "Alles bereit? →",
        ctaRoute: `/trip/${trip.id}?tab=overview`,
        urgency: "high",
      };

    case "travel_day":
      return {
        phaseLabel: "Heute geht's los! 🎉",
        title: nextItem ? "Erster Punkt: " + nextItem.title : "Heute ist Abreisetag!",
        description: nextItem
          ? `Um ${nextItem.start_time ? formatTime(nextItem.start_time) : "?"}${nextItem.location ? " · " + nextItem.location : ""}`
          : "Gute Reise! Schau dir das Programm an.",
        ctaLabel: "Los geht's! →",
        ctaRoute: `/trip/${trip.id}?tab=program`,
        urgency: "high",
      };

    case "in_progress":
      return {
        phaseLabel: "Unterwegs ✈️",
        title: nextItem ? nextItem.title : "Geniesse die Reise!",
        description: nextItem
          ? `${nextItem.start_time ? formatTime(nextItem.start_time) : ""}${nextItem.location ? " · " + nextItem.location : ""}`
          : "Kein weiterer Programmpunkt heute.",
        ctaLabel: nextItem ? "Auf Karte zeigen →" : "Programm ansehen →",
        ctaRoute: nextItem ? `/trip/${trip.id}?tab=map` : `/trip/${trip.id}?tab=program`,
        urgency: "low",
      };

    case "last_day":
      return {
        phaseLabel: "Letzter Reisetag",
        title: "Auf geht's nach Hause! 🏠",
        description: "Danach kannst du die Abrechnung abschliessen und offene Beträge klären.",
        ctaLabel: "Abrechnung starten →",
        ctaRoute: `/trip/${trip.id}?tab=accounting`,
        urgency: "medium",
      };

    case "post_trip_fresh":
      return {
        phaseLabel: "Gerade zurück",
        title: "Abrechnung noch offen",
        description: daysAfterEnd === 0
          ? "Willkommen zurück! Jetzt offene Beträge klären."
          : `Seit ${daysAfterEnd} Tagen zurück – Abrechnung abschliessen?`,
        ctaLabel: "Abrechnung abschliessen →",
        ctaRoute: `/trip/${trip.id}?tab=accounting`,
        urgency: "high",
      };

    case "post_trip":
      return {
        phaseLabel: "Reise abgeschlossen",
        title: "Schöne Erinnerungen! 📸",
        description: `${daysAfterEnd} Tage her – schau dir deine Highlights an.`,
        ctaLabel: "Erinnerungen ansehen →",
        ctaRoute: `/trip/${trip.id}?tab=memories`,
        urgency: "low",
      };
  }
}

// Phasen die nur das Orga-Team sehen soll
const ORGANIZER_ONLY_PHASES: TripPhase[] = ["pre_trip_early", "last_day", "post_trip_fresh"];

export default function PhaseContextCard({ trip, programItems = [], isOrganizer }: PhaseContextCardProps) {
  const phase = getTripPhase(trip.start_date, trip.end_date);

  if (ORGANIZER_ONLY_PHASES.includes(phase) && !isOrganizer) return null;

  const content = getCardContent(phase, trip, programItems);

  return (
    <div className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-1">
            {content.phaseLabel}
          </p>
          <h3 className="text-base font-semibold text-slate-100 mb-1">{content.title}</h3>
          <p className="text-sm text-slate-400">{content.description}</p>
        </div>
        {content.urgency === "high" && (
          <span className="text-amber-400 text-lg flex-shrink-0 mt-0.5">⏰</span>
        )}
        {content.urgency === "medium" && (
          <span className="text-sky-400 text-lg flex-shrink-0 mt-0.5">📋</span>
        )}
      </div>
      <Link
        to={content.ctaRoute}
        className="mt-3 inline-block bg-sky-500 hover:bg-sky-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"
      >
        {content.ctaLabel}
      </Link>
    </div>
  );
}
